import { createRef, useEffect, ChangeEvent } from "react";
import { Grid } from "@mui/material";
import { AppTextField } from "src/Pages/components";
import { FormatMoney, handleFormatPercent, handleFormatToNumeric } from "src/shared/Utils/FormatMoney";
import { CardAlerts } from "src/Pages/components/CardAlerts/CardAlerts";
import { InfoOutlinedIcon } from "src/Pages/components/Icons/Icons";

interface IFormEditarParcela {
  values: {
    parcela: number;
    beneficiario: string;
    cpf: string;
    vidas: number;
    vendedor: string;
    codVenda: number;
    vencimento: string;
    valorBoleta: number;
    valorSistema?: number;
    comissaoCorretora: number;
    comissaoCorretor: number;
    comissaoSupervisor: number;
    comissaoGerente: number;
    lucroParcela: number;
    porcentagemCorretora: number;
    porcentagemCorretor: number;
    porcentagemSupervisor: number;
    porcentagemGerente: number;
  };
  setValues: React.Dispatch<React.SetStateAction<any>>;
}

export const FormEditarParcela = ({ values, setValues }: IFormEditarParcela) => {
  const refOrigem = createRef<HTMLInputElement>();
  const handleFocusedID = () => refOrigem.current?.focus();

  useEffect(() => {
    handleFocusedID();
  }, []);

  const handleChange =
    (
      prop: keyof typeof values,
      option?: "limitNumber" | "money" | "percent"
    ) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        if (option === "money") {
          setValues({
            ...values,
            [prop]: FormatMoney(event.target.value),
          });
        } else if (option === "limitNumber") {
          if (event.target.value.length <= 5) {
            setValues({
              ...values,
              [prop]: Number(event.target.value) || 0,
            });
          }
        } else if (option === "percent") {
          if (event.target.value.length <= 7) {
            setValues({
              ...values,
              [prop]: Number(handleFormatPercent(event.target.value)) || 0,
            });
          }
        } else if (prop === "vidas" || prop === "parcela" || prop === "codVenda") {
          setValues({
            ...values,
            [prop]: Number(event.target.value) || 0,
          });
        } else if (prop === "vencimento") {
          setValues({
            ...values,
            [prop]: event.target.value,
          });
        } else {
          setValues({
            ...values,
            [prop]: event.target.value,
          });
        }
      };

  // Calcular valores de comissão baseado nas porcentagens
  useEffect(() => {
    const valorBaseString = typeof values.valorBoleta === "string" 
      ? values.valorBoleta 
      : String(values.valorBoleta);
    
    const valorBase = Number(handleFormatToNumeric(valorBaseString)) || 0;

    const calcComissaoCorretora = (valorBase * values.porcentagemCorretora) / 100;
    const calcComissaoCorretor = (valorBase * values.porcentagemCorretor) / 100;
    const calcComissaoSupervisor = (valorBase * values.porcentagemSupervisor) / 100;
    const calcComissaoGerente = (valorBase * values.porcentagemGerente) / 100;

    setValues({
      ...values,
      comissaoCorretora: calcComissaoCorretora,
      comissaoCorretor: calcComissaoCorretor,
      comissaoSupervisor: calcComissaoSupervisor,
      comissaoGerente: calcComissaoGerente,
    });
  }, [
    values.valorBoleta,
    values.porcentagemCorretora,
    values.porcentagemCorretor,
    values.porcentagemSupervisor,
    values.porcentagemGerente,
  ]);

  return (
    <>
      {values && (
        <Grid container spacing={2} sx={{ marginBottom: "1.5rem" }}>
          <Grid container item spacing={2}>
            <Grid item xs={8}>
              <AppTextField
                fullWidth
                label="Cód. Proposta"
                sxStyle={{ opacity: ".5", pointerEvents: "none", margin: 0 }}
                value={values?.codVenda}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={4}>
              <AppTextField
                fullWidth
                label="Parcela *"
                inputRef={refOrigem}
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={values.parcela}
                variant="outlined"
                type="number"
                onChange={handleChange("parcela")}
              />
            </Grid>
          </Grid>

          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <AppTextField
                fullWidth
                label="Vencimento *"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                type="date"
                value={values?.vencimento}
                variant="outlined"
                onChange={handleChange("vencimento")}
              />
            </Grid>
            <Grid item xs={6}>
              <AppTextField
                fullWidth
                label="Vidas *"
                type="number"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={values?.vidas}
                variant="outlined"
                onChange={handleChange("vidas")}
              />
            </Grid>
          </Grid>

          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <AppTextField
                fullWidth
                label="Valor Mensalidade *"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={
                  typeof values?.valorBoleta === "string"
                    ? values.valorBoleta
                    : FormatMoney(String(values?.valorBoleta || 0))
                }
                variant="outlined"
                onChange={handleChange("valorBoleta", "money")}
              />
            </Grid>

            <Grid container item spacing={2}>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Corretora *"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={values?.porcentagemCorretora}
                  variant="outlined"
                  onChange={handleChange("porcentagemCorretora", "percent")}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Corretor *"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={values?.porcentagemCorretor}
                  variant="outlined"
                  onChange={handleChange("porcentagemCorretor", "percent")}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Supervisor"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={values?.porcentagemSupervisor}
                  variant="outlined"
                  onChange={handleChange("porcentagemSupervisor", "percent")}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Gerente"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={values?.porcentagemGerente}
                  variant="outlined"
                  onChange={handleChange("porcentagemGerente", "percent")}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container item spacing={2}>
            <Grid item xs={3}>
              <AppTextField
                fullWidth
                label="R$ Corretora"
                variant="outlined"
                disabled
                sxStyle={{ opacity: ".7", pointerEvents: "none", margin: 0 }}
                value={FormatMoney(String(values.comissaoCorretora))}
              />
            </Grid>
            <Grid item xs={3}>
              <AppTextField
                fullWidth
                label="R$ Corretor"
                variant="outlined"
                disabled
                sxStyle={{ opacity: ".7", pointerEvents: "none", margin: 0 }}
                value={FormatMoney(String(values.comissaoCorretor))}
              />
            </Grid>
            <Grid item xs={3}>
              <AppTextField
                fullWidth
                label="R$ Supervisor"
                variant="outlined"
                disabled
                sxStyle={{ opacity: ".7", pointerEvents: "none", margin: 0 }}
                value={FormatMoney(String(values.comissaoSupervisor))}
              />
            </Grid>
            <Grid item xs={3}>
              <AppTextField
                fullWidth
                label="R$ Gerente"
                variant="outlined"
                disabled
                sxStyle={{ opacity: ".7", pointerEvents: "none", margin: 0 }}
                value={FormatMoney(String(values.comissaoGerente))}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      {values && String(values?.parcela) === "0" && (
        <CardAlerts
          title="Parcela numero 0 é considerada como parcela de adesão !"
          subTitle="Ela é uma parcela normal - Todas as ações estão habilitadas, incluindo edição e geração de comissão !"
          color="primary"
          icon={<InfoOutlinedIcon fontSize="small" />}
        />
      )}
    </>
  );
};

